import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import {
  getApplicationDocuments,
  getSignedUrl,
  compareDocuments,
  updatePedsRecord
} from '../../api/PedsReview';
import PDFViewer from '../PDFViewer';

const PedsReviewDetail = () => {
  const history = useHistory();
  const location = useLocation<any>();

  const initialRow = location.state?.row || {};
  const [formData, setFormData] = useState<any>({ ...initialRow });
  const [originalData, setOriginalData] = useState<any>({ ...initialRow });
  const [updating, setUpdating] = useState<boolean>(false);
  const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
  const [originalDocSignedUrl, setOriginalDocSignedUrl] = useState<string>('');
  const [loadingSignedUrl, setLoadingSignedUrl] = useState<boolean>(false);
  const [mode, setMode] = useState<'edit' | 'selectDocs'>('edit');
  const [documents, setDocuments] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState<boolean>(false);
  const [docsError, setDocsError] = useState<string>('');
  const [compareOpen, setCompareOpen] = useState<boolean>(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [compareLoading, setCompareLoading] = useState<boolean>(false);
  const [comparisonUrl, setComparisonUrl] = useState<string>('');
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [duplicateLoading, setDuplicateLoading] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => {
      const newData = { ...prev, [field]: value };
      // Check if there are changes after this update
      const hasDataChanges = Object.keys(newData).some(key => newData[key] !== originalData[key]);
      setHasChanges(hasDataChanges);
      return newData;
    });
  };

  // Function to get only the fields that have changed
  const getChangedFields = () => {
    const changedFields: Record<string, any> = {};

    Object.keys(formData).forEach(key => {
      if (formData[key] !== originalData[key]) {
        changedFields[key] = formData[key];
      }
    });

    return changedFields;
  };

  // Function to save changes to the record
  const handleSaveChanges = async () => {
    if (!currentUser?.curator_email || !currentUser?.curator_name) {
      console.error('User information not available');
      return;
    }

    const changedFields = getChangedFields();

    if (Object.keys(changedFields).length === 0) {
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
      return;
    }

    setUpdating(true);
    setUpdateSuccess(false);

    try {
      const payload = {
        id: formData.id || originalData.id,
        fields: {
          ...changedFields,
          curator_email: currentUser.curator_email,
          curator_name: currentUser.curator_name
        }
      };

      const response = await updatePedsRecord(payload);

      if (response.data && !response.data.error) {
        // Update originalData to reflect the saved state
        setOriginalData({ ...formData });
        setHasChanges(false);
        setUpdateSuccess(true);
        setTimeout(() => setUpdateSuccess(false), 3000);
      } else {
        throw new Error(response.data?.message || 'Failed to update record');
      }
    } catch (error) {
      console.error('Error updating record:', error);
    } finally {
      setUpdating(false);
    }
  };

  // Function to switch to document selection mode
  const handleSelectDocuments = async () => {
    // Save changes first if there are any changes
    if (hasChanges) {
      if (!currentUser?.curator_email || !currentUser?.curator_name) {
        console.error('User information not available');
        return;
      }

      try {
        const changedFields = getChangedFields();

        if (Object.keys(changedFields).length > 0) {
          setUpdating(true);
          const payload = {
            id: formData.id || originalData.id,
            fields: {
              ...changedFields,
              curator_email: currentUser.curator_email,
              curator_name: currentUser.curator_name
            }
          };

          const response = await updatePedsRecord(payload);

          if (response.data && !response.data.error) {
            // Update originalData to reflect the saved state
            setOriginalData({ ...formData });
            setHasChanges(false);
            setUpdateSuccess(true);
            setTimeout(() => setUpdateSuccess(false), 3000);
          } else {
            throw new Error(response.data?.message || 'Failed to update record');
          }
          setUpdating(false);
        }
      } catch (error) {
        console.error('Error saving changes before viewing documents:', error);
        setUpdating(false);
        return; // Don't proceed to fetch documents if save failed
      }
    }

    // Switch to document selection mode immediately
    setMode('selectDocs');

    // Then fetch documents using current form data
    setDocsLoading(true);
    setDocsError('');
    setDocuments([]); // Clear previous documents

    try {
      // Use current formData values (which might have been updated)
      const applNum = (formData.application_number || '').toString();
      const submissionType = (formData.submission_type || '').toString();
      const submissionNum = (formData.submission_no || '').toString();

      if (!applNum || !submissionType || !submissionNum) {
        setDocsError('Application number, submission type, and submission number are required');
      } else {
        const resp: any = await getApplicationDocuments(applNum, submissionNum, submissionType);
        const list: any[] = resp?.data?.body?.application_docs || [];
        setDocuments(list);
      }
    } catch (_e) {
      setDocsError('Failed to fetch documents from peds-admin API');
    } finally {
      setDocsLoading(false);
    }
  };

  // Fetch current user information
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const authUser = await Auth.currentUserInfo();
        if (authUser) {
          setCurrentUser({
            curator_name: authUser.attributes['custom:user'],
            curator_email: authUser.attributes.email
          });
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    getCurrentUser();
  }, []);

  // Fetch signed URL whenever s3_path changes
  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (!formData?.s3_path) {
        setOriginalDocSignedUrl('');
        return;
      }
      setLoadingSignedUrl(true);
      try {
        const response: any = await getSignedUrl(formData.s3_path, 'peds');
        const signedUrl = response?.data?.body?.s3_url;
        setOriginalDocSignedUrl(signedUrl || '');
      } catch (error) {
        setOriginalDocSignedUrl('');
      } finally {
        setLoadingSignedUrl(false);
      }
    };
    fetchSignedUrl();
  }, [formData?.s3_path]);

  const handleCompareDocuments = async () => {
    if (!selectedDoc || !originalDocSignedUrl) {
      return;
    }

    setCompareLoading(true);
    setComparisonUrl('');

    try {
      // Get the s3_url from selected document and sign it with proper center
      const selectedDocS3Url = selectedDoc.s3_url || selectedDoc.url;
      if (!selectedDocS3Url) {
        throw new Error('Selected document does not have a valid s3_url');
      }

      // Use center from the selected row, fallback to 'peds' if not available
      const center = formData.center?.toLowerCase() || 'peds';

      // Sign the selected document's s3_url
      const signedUrlResponse: any = await getSignedUrl(selectedDocS3Url, center);
      const childObjectUrl = signedUrlResponse?.data?.body?.s3_url;

      if (!childObjectUrl) {
        throw new Error('Failed to get signed URL for selected document');
      }

      const payload = {
        child_object: childObjectUrl,
        parent_object: originalDocSignedUrl
      };

      const response: any = await compareDocuments(payload);

      if (response.data && response.data.body && response.data.body.success) {
        const draftableUrl = response.data.body.success;
        setComparisonUrl(draftableUrl);
        setCompareOpen(true);
      } else {
        console.error('Unexpected response structure:', response);
        throw new Error(
          response.data?.body?.error || response.data?.Error || 'Failed to generate comparison'
        );
      }
    } catch (error) {
      console.error('Error comparing documents:', error);
    } finally {
      setCompareLoading(false);
    }
  };

  const handleCloseComparison = () => {
    setCompareOpen(false);
    setComparisonUrl('');
  };

  const handleBackButton = () => {
    if (mode === 'selectDocs') {
      // Go back to edit mode
      setMode('edit');
      setSelectedDoc(null);
      setDocuments([]);
      setDocsError('');
    } else {
      // Go back to previous page (datagrid)
      history.goBack();
    }
  };

  const handleSetDuplicate = async (desiredStatus: boolean) => {
    if (!currentUser?.curator_email || !currentUser?.curator_name) {
      console.error('User information not available');
      return;
    }

    setDuplicateLoading(true);

    try {
      const payload = {
        id: formData.id || originalData.id,
        fields: {
          is_duplicate: desiredStatus,
          curator_email: currentUser.curator_email,
          curator_name: currentUser.curator_name
        }
      };

      const response = await updatePedsRecord(payload);

      if (response.data && !response.data.error) {
        // Update the form data and original data to reflect the change
        setFormData((prev: any) => ({ ...prev, is_duplicate: desiredStatus }));
        setOriginalData((prev: any) => ({ ...prev, is_duplicate: desiredStatus }));
        setUpdateSuccess(true);
        setTimeout(() => setUpdateSuccess(false), 3000);
      } else {
        throw new Error(response.data?.message || 'Failed to update duplicate status');
      }
    } catch (error) {
      console.error('Error updating duplicate status:', error);
    } finally {
      setDuplicateLoading(false);
    }
  };

  return (
    <>
      <Box display='flex' alignItems='center' p={2} mt={6}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackButton}>
          {mode === 'selectDocs' ? 'Back to Edit' : 'Back'}
        </Button>
        <Box flexGrow={1} />
      </Box>

      {updateSuccess && (
        <Box px={2}>
          <Alert severity='success'>Changes saved successfully!</Alert>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Left Pane: Edit form or Documents selection */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '80vh' }}>
            <CardContent
              sx={{ height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <Typography variant='h6' gutterBottom>
                {mode === 'edit' ? 'Edit Peds Review Data' : 'Select a Document to Compare'}
              </Typography>
              {mode === 'edit' ? (
                <Box sx={{ flex: 1, overflow: 'auto', pt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Application Number'
                        value={formData.application_number || ''}
                        onChange={e => handleInputChange('application_number', e.target.value)}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label='Center'
                        value={formData.center || ''}
                        onChange={e => handleInputChange('center', e.target.value)}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label='Submission No'
                        value={formData.submission_no || ''}
                        onChange={e => handleInputChange('submission_no', e.target.value)}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Submission Type'
                        value={formData.submission_type || ''}
                        onChange={e => handleInputChange('submission_type', e.target.value)}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Title'
                        value={formData.title || ''}
                        onChange={e => handleInputChange('title', e.target.value)}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Filename'
                        value={formData.filename || ''}
                        onChange={e => handleInputChange('filename', e.target.value)}
                        variant='outlined'
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Document URL'
                        value={formData.url || ''}
                        onChange={e => handleInputChange('url', e.target.value)}
                        variant='outlined'
                        size='small'
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='S3 Path'
                        value={formData.s3_path || ''}
                        onChange={e => handleInputChange('s3_path', e.target.value)}
                        variant='outlined'
                        size='small'
                        helperText='S3 path for the original document'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Submission Date'
                        value={formData.submission_date || ''}
                        onChange={e => handleInputChange('submission_date', e.target.value)}
                        variant='outlined'
                        size='small'
                        type='date'
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Page Count'
                        value={formData.page_count || ''}
                        onChange={e => handleInputChange('page_count', e.target.value)}
                        variant='outlined'
                        size='small'
                        type='number'
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  {docsLoading && (
                    <Box display='flex' alignItems='center' justifyContent='center' flex={1}>
                      <Typography color='textSecondary'>Loading documents...</Typography>
                    </Box>
                  )}
                  {docsError && <Alert severity='error'>{docsError}</Alert>}
                  {!docsLoading && !docsError && documents.length === 0 && (
                    <Box display='flex' alignItems='center' justifyContent='center' flex={1}>
                      <Typography color='textSecondary'>No Documents Available</Typography>
                    </Box>
                  )}
                  {!docsLoading && !docsError && documents.length > 0 && (
                    <List dense sx={{ flex: 1, overflow: 'auto' }}>
                      {documents.map((doc: any, index: number) => (
                        <ListItem
                          key={index}
                          button
                          selected={selectedDoc?.document_id === doc.document_id}
                          onClick={() => setSelectedDoc(doc)}>
                          <ListItemText
                            primary={
                              doc.filename ||
                              doc.title ||
                              doc.document_title ||
                              `Document ${index + 1}`
                            }
                            secondary={(doc.url || doc.s3_url || '').toString()}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}
              {mode === 'edit' && (
                <Box mt={2} display='flex' justifyContent='flex-end'>
                  <Button
                    variant='contained'
                    color='secondary'
                    startIcon={<SaveIcon />}
                    onClick={handleSaveChanges}
                    disabled={updating || !hasChanges || !currentUser}
                    style={{ marginRight: 8 }}>
                    {updating ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant='outlined'
                    color='primary'
                    onClick={handleSelectDocuments}
                    disabled={updating || (hasChanges && !currentUser)}>
                    {hasChanges ? 'Save & View Documents' : 'View Documents'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* PDF Viewer for selected row document */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '80vh' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography variant='h6'>Document Preview</Typography>
                {mode === 'selectDocs' && (
                  <Box display='flex' gap={1}>
                    <Button
                      variant={formData.is_duplicate ? 'contained' : 'outlined'}
                      color='warning'
                      size='small'
                      onClick={() => handleSetDuplicate(true)}
                      disabled={duplicateLoading || !currentUser}
                      style={{ marginRight: 8 }}>
                      {duplicateLoading ? 'Updating...' : 'Mark Duplicate'}
                    </Button>
                    <Button
                      variant={formData.is_duplicate === false ? 'contained' : 'outlined'}
                      color='error'
                      size='small'
                      onClick={() => handleSetDuplicate(false)}
                      disabled={duplicateLoading || !currentUser}>
                      {duplicateLoading ? 'Updating...' : 'Mark Not Duplicate'}
                    </Button>
                  </Box>
                )}
              </Box>
              {loadingSignedUrl && (
                <Box flex={1} display='flex' alignItems='center' justifyContent='center'>
                  <Typography color='textSecondary'>Loading document...</Typography>
                </Box>
              )}
              {!loadingSignedUrl && originalDocSignedUrl && (
                <Box
                  flex={1}
                  sx={{
                    height: '100%',
                    overflow: 'auto',
                    position: 'relative'
                  }}>
                  <PDFViewer fileUrl={originalDocSignedUrl} />
                </Box>
              )}
              {!loadingSignedUrl && !originalDocSignedUrl && (
                <Box flex={1} display='flex' alignItems='center' justifyContent='center'>
                  <Typography color='textSecondary'>
                    {formData.s3_path ? 'Failed to load document' : 'No document available'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Compare button at the bottom when in selectDocs mode */}
      {mode === 'selectDocs' && (
        <Box display='flex' justifyContent='center' p={2}>
          <Button
            variant='contained'
            color='primary'
            size='large'
            onClick={handleCompareDocuments}
            disabled={!selectedDoc || !originalDocSignedUrl || compareLoading}>
            {compareLoading ? 'Comparing...' : 'Compare Documents'}
          </Button>
        </Box>
      )}

      <Dialog open={compareOpen} onClose={handleCloseComparison} maxWidth='xl' fullWidth>
        <DialogTitle>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Typography variant='h6'>Document Comparison</Typography>
            <IconButton onClick={handleCloseComparison}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box height='80vh'>
            {comparisonUrl ? (
              <iframe
                style={{ height: '100%', width: '100%', border: 'none' }}
                src={comparisonUrl}
                title='Document Comparison'
                allow='fullscreen'
                sandbox='allow-same-origin allow-scripts allow-forms allow-popups'
              />
            ) : (
              <Box display='flex' alignItems='center' justifyContent='center' height='100%'>
                <Typography color='textSecondary'>
                  {compareLoading ? 'Loading comparison...' : 'No comparison available'}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PedsReviewDetail;
